import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api, { formatDate } from '../lib/api';
import { CalendarDays, Clock, Users, Check, X, ArrowLeft } from 'lucide-react';

function toDirectImg(url) {
  if (!url) return url;
  const m1 = url.match(/drive\.google\.com\/file\/d\/([^/]+)\//);
  if (m1) return `https://drive.google.com/uc?export=view&id=${m1[1]}`;
  const m2 = url.match(/drive\.google\.com\/open\?id=([^&]+)/);
  if (m2) return `https://drive.google.com/uc?export=view&id=${m2[1]}`;
  return url;
}

const EMPTY_FORM = { customer_name: '', email: '', phone: '', persons: 2, note: '' };

export default function EventsPublic() {
  const [events, setEvents] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoFailed, setLogoFailed] = useState(false);

  const [bookFor, setBookFor] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [privacy, setPrivacy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/public/events')
      .then(r => setEvents(r.data?.events || []))
      .catch(() => toast.error('Veranstaltungen konnten nicht geladen werden.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get('/settings/public').then(r => setSettings(r.data)).catch(() => {});
  }, []);

  const openBooking = (e) => {
    setBookFor(e); setForm({ ...EMPTY_FORM, persons: Math.min(2, e.available_seats) || 1 });
    setPrivacy(false); setSuccess(null);
  };

  const submit = async () => {
    if (!form.customer_name.trim()) return toast.error('Bitte Ihren Namen angeben.');
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return toast.error('Bitte eine gültige E-Mail-Adresse angeben.');
    if (!(Number(form.persons) >= 1)) return toast.error('Bitte die Personenzahl angeben.');
    if (settings?.privacy_checkbox_required && !privacy) return toast.error('Bitte bestätigen Sie den Datenschutzhinweis.');
    setSubmitting(true);
    try {
      const r = await api.post(`/events/${bookFor.id}/book`, {
        customer_name: form.customer_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        persons: Number(form.persons),
        note: form.note.trim(),
        privacy_accepted: privacy,
      });
      setSuccess({ persons: Number(form.persons), event_name: r.data?.event_name || bookFor.name });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Buchung fehlgeschlagen.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="events-public-page">
      {/* Hero */}
      <div
        className="min-h-[32vh] md:min-h-[38vh] relative bg-cover bg-center bg-[#1C1C19]"
        style={settings?.booking_image_url ? { backgroundImage: `linear-gradient(180deg, rgba(28,28,25,0.2) 0%, rgba(28,28,25,0.0) 45%, rgba(28,28,25,0.9) 100%), url("${toDirectImg(settings.booking_image_url)}")` } : {}}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#1C1C19]/30 via-transparent to-[#1C1C19]/80"></div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 pt-8 pb-14 flex flex-col h-full min-h-[32vh] md:min-h-[38vh]">
          <div className="flex items-center justify-between text-white">
            {settings?.logo_url && !logoFailed ? (
              <img src={toDirectImg(settings.logo_url)} alt={settings?.logo_text || 'Logo'} onError={() => setLogoFailed(true)} className="h-8 md:h-10 w-auto object-contain" data-testid="events-logo" />
            ) : (
              <span className="font-brand text-3xl md:text-4xl tracking-tight">{settings?.logo_text || settings?.restaurant_name || ''}<span className="text-[#EAB308]">.</span></span>
            )}
            <Link to="/" data-testid="events-back-link" className="text-sm text-white/90 hover:text-white flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Zur Reservierung</Link>
          </div>
          <div className="mt-auto text-white">
            <p className="text-sm md:text-base uppercase tracking-[0.25em] text-white/80 mb-3">Besondere Anlässe</p>
            <h1 className="font-display text-xl sm:text-2xl lg:text-3xl leading-snug max-w-2xl text-white/95">Veranstaltungen & Events</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-6 -mt-10 pb-20 relative z-20">
        {loading ? (
          <div className="bg-white border border-[#E5E5E0] shadow-lg rounded-lg p-10 text-center text-[#9CA3AF]">Lädt…</div>
        ) : events.length === 0 ? (
          <div className="bg-white border border-[#E5E5E0] shadow-lg rounded-lg p-10 text-center" data-testid="events-empty">
            <CalendarDays className="w-10 h-10 text-[#E5E5E0] mx-auto mb-3" />
            <p className="text-[#5C5C59]">Aktuell sind keine Veranstaltungen buchbar.</p>
            <Link to="/" className="inline-block mt-4 text-[#B45309] hover:underline text-sm">Zur normalen Tischreservierung →</Link>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {events.map(e => (
              <div key={e.id} className="bg-white border border-[#E5E5E0] shadow-sm rounded-lg p-5 flex flex-col animate-fade-up" data-testid={`event-card-${e.id}`}>
                <h2 className="text-lg font-medium text-[#1C1C19]">{e.name}</h2>
                <div className="text-sm text-[#5C5C59] mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="flex items-center gap-1"><CalendarDays className="w-4 h-4 text-[#EAB308]" /> {formatDate(e.date)}</span>
                  <span className="flex items-center gap-1"><Clock className="w-4 h-4 text-[#EAB308]" /> {e.start_time}–{e.end_time}</span>
                </div>
                {e.description && <p className="text-sm text-[#5C5C59] mt-3 flex-1 whitespace-pre-line">{e.description}</p>}
                <div className="mt-4 flex items-center justify-between gap-3">
                  {e.sold_out ? (
                    <span className="text-sm font-medium text-[#DC2626] flex items-center gap-1" data-testid={`event-soldout-${e.id}`}>Ausverkauft</span>
                  ) : (
                    <span className="text-sm text-[#16A34A] flex items-center gap-1"><Users className="w-4 h-4" /> {e.available_seats} {e.available_seats === 1 ? 'Platz' : 'Plätze'} frei</span>
                  )}
                  <button onClick={() => openBooking(e)} disabled={e.sold_out} data-testid={`event-book-${e.id}`}
                    className={`px-4 py-2 rounded text-sm font-medium ${e.sold_out ? 'bg-[#E5E5E0] text-[#9CA3AF] cursor-not-allowed' : 'btn-primary'}`}>
                    {e.sold_out ? 'Ausgebucht' : 'Jetzt buchen'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {bookFor && (
        <div className="fixed inset-0 bg-[#1C1C19]/50 z-50 flex items-center justify-center p-4" onClick={() => setBookFor(null)}>
          <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={ev => ev.stopPropagation()} data-testid="event-booking-dialog">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-medium">{success ? 'Buchung bestätigt' : bookFor.name}</h2>
              <button onClick={() => setBookFor(null)} data-testid="event-booking-close" className="text-[#9CA3AF] hover:text-[#1C1C19]"><X className="w-5 h-5" /></button>
            </div>

            {success ? (
              <div className="text-center py-4" data-testid="event-booking-success">
                <div className="w-14 h-14 rounded-full bg-[#16A34A]/15 flex items-center justify-center mx-auto mb-3"><Check className="w-7 h-7 text-[#16A34A]" /></div>
                <p className="text-[#1C1C19] font-medium">Vielen Dank für Ihre Buchung!</p>
                <p className="text-sm text-[#5C5C59] mt-2">Sie haben <b>{success.persons}</b> {success.persons === 1 ? 'Platz' : 'Plätze'} für <b>{success.event_name}</b> gebucht.</p>
                <p className="text-xs text-[#9CA3AF] mt-2">Eine Bestätigung geht Ihnen zu. Bei Fragen kontaktieren Sie uns bitte.</p>
                <button onClick={() => setBookFor(null)} className="btn-primary px-5 py-2 rounded mt-5">Schließen</button>
              </div>
            ) : (
              <>
                <p className="text-sm text-[#5C5C59] mb-4">{formatDate(bookFor.date)} · {bookFor.start_time}–{bookFor.end_time} · {bookFor.available_seats} Plätze frei</p>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium block mb-1">Anzahl Personen *</label>
                    <select value={form.persons} onChange={e => setForm({ ...form, persons: e.target.value })} data-testid="event-booking-persons" className="w-full px-3 py-2 border border-[#E5E5E0] rounded bg-white">
                      {Array.from({ length: Math.max(1, bookFor.available_seats) }, (_, i) => i + 1).map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">Name *</label>
                    <input value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} data-testid="event-booking-name" className="w-full px-3 py-2 border border-[#E5E5E0] rounded" />
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">E-Mail *</label>
                    <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="event-booking-email" className="w-full px-3 py-2 border border-[#E5E5E0] rounded" />
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">Telefon</label>
                    <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="event-booking-phone" className="w-full px-3 py-2 border border-[#E5E5E0] rounded" />
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">Anmerkung</label>
                    <textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} rows={2} data-testid="event-booking-note" placeholder="z. B. Allergien (optional)" className="w-full px-3 py-2 border border-[#E5E5E0] rounded" />
                  </div>
                  <label className="flex items-start gap-2 text-sm text-[#5C5C59] cursor-pointer">
                    <input type="checkbox" checked={privacy} onChange={e => setPrivacy(e.target.checked)} data-testid="event-booking-privacy" className="w-4 h-4 mt-0.5 accent-[#EAB308]" />
                    <span>Ich habe die <Link to="/datenschutz" target="_blank" className="text-[#B45309] underline">Datenschutzhinweise</Link> gelesen und akzeptiere sie.</span>
                  </label>
                </div>
                <div className="flex gap-2 mt-5">
                  <button onClick={() => setBookFor(null)} className="flex-1 py-2 rounded border border-[#E5E5E0]">Abbrechen</button>
                  <button onClick={submit} disabled={submitting} data-testid="event-booking-submit" className="flex-1 btn-primary py-2 rounded">{submitting ? 'Bucht…' : 'Verbindlich buchen'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
